import { TEMPLATE_CATEGORIES } from '@/types/templates';
import { FileText, Play } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/**
 * Helper to format WhatsApp text with bold, italic, strikethrough, and monospace
 */
const WhatsAppText = ({ text }) => {
  if (!text) return null;

  // Replace variables first if they are not already replaced
  // This is a safety measure

  const parts = text.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~|```[^`]+```)/g);

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
          return (
            <strong key={index} className="font-bold">
              {part.slice(1, -1)}
            </strong>
          );
        }
        if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
          return (
            <em key={index} className="italic">
              {part.slice(1, -1)}
            </em>
          );
        }
        if (part.startsWith('~') && part.endsWith('~') && part.length > 2) {
          return (
            <span key={index} className="line-through">
              {part.slice(1, -1)}
            </span>
          );
        }
        if (part.startsWith('```') && part.endsWith('```') && part.length > 6) {
          return (
            <code
              key={index}
              className="bg-gray-100 px-1 rounded font-mono text-[12px]"
            >
              {part.slice(3, -3)}
            </code>
          );
        }
        return part;
      })}
    </>
  );
};

/**
 * Unified Template Preview Component
 * Handles real-time preview for all template types and locations
 */
const TemplatePreview = ({
  template,
  headerParams = {},
  bodyParams = {},
  buttonParams = {},
  headerFile = null,
  showTail = true,
  className = '',
  showMobilePreview = false,
  businessName = 'Business',
  businessLogo = null,
}) => {
  const [localMediaUrl, setLocalMediaUrl] = useState(null);

  useEffect(() => {
    if (headerFile && headerFile instanceof File) {
      const url = URL.createObjectURL(headerFile);
      setLocalMediaUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLocalMediaUrl(null);
    }
  }, [headerFile]);

  // Replace parameters in text
  const replaceParams = (text, params) => {
    if (!text) return '';

    // Handle both {{1}} and {1} formats
    return text
      .replace(/\{\{(\d+)\}\}/g, (match, p1) => {
        const idx = parseInt(p1);
        if (Array.isArray(params)) {
          return params[idx - 1] || match;
        }
        return params[p1] || match;
      })
      .replace(/\{(\d+)\}/g, (match, p1) => {
        const idx = parseInt(p1);
        if (Array.isArray(params)) {
          return params[idx - 1] || match;
        }
        return params[p1] || match;
      });
  };

  // Header Content
  const headerContent = useMemo(() => {
    if (!template?.header) return null;
    const { format, text } = template.header;

    if (format === 'TEXT' && text) {
      const replacedText = replaceParams(text, headerParams);
      return (
        <div className="font-bold text-gray-900 text-[14px] mb-1 px-3 pt-3">
          <WhatsAppText text={replacedText} />
        </div>
      );
    }

    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
      const mediaUrl = template?.header?.signedUrl || localMediaUrl;

      return (
        <div className="aspect-[16/9] bg-gray-100 flex flex-col items-center justify-center relative overflow-hidden border-b border-gray-50">
          {mediaUrl ? (
            format === 'IMAGE' ? (
              <img
                src={mediaUrl}
                alt="Header"
                className="w-full h-full object-cover"
              />
            ) : format === 'VIDEO' ? (
              <div className="relative w-full h-full">
                <video src={mediaUrl} className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                  <Play className="w-10 h-10 text-white fill-white" />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center p-4">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-2">
                  <FileText className="w-6 h-6 text-primary-600" />
                </div>
                <span className="text-[11px] text-gray-500 font-medium truncate max-w-[200px]">
                  {headerFile?.name || 'Document.pdf'}
                </span>
              </div>
            )
          ) : (
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mb-2 mx-auto">
                {format === 'IMAGE' ? (
                  <Play className="w-5 h-5 text-gray-400 rotate-90" />
                ) : (
                  <Play className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                {format} Required
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  }, [template, headerParams, headerFile, localMediaUrl]);

  // Body Content
  const bodyContent = useMemo(() => {
    if (template?.category === TEMPLATE_CATEGORIES.AUTHENTICATION) {
      return (
        <div className="px-3 py-2 text-[13px] text-gray-800">
          <WhatsAppText text="{{1}} is your verification code. For your security, do not share this code." />
        </div>
      );
    }

    if (!template?.body?.text) {
      return (
        <div className="px-3 py-2">
          <span className="text-gray-300 italic text-[13px]">
            Message body will appear here...
          </span>
        </div>
      );
    }

    const replacedText = replaceParams(template.body.text, bodyParams);
    return (
      <div className="px-3 py-2 text-[13px] text-gray-800 whitespace-pre-wrap leading-relaxed">
        <WhatsAppText text={replacedText} />
      </div>
    );
  }, [template, bodyParams]);

  // Footer Content
  const footerContent = useMemo(() => {
    let footerText = template?.footer?.text;

    if (template?.category === TEMPLATE_CATEGORIES.AUTHENTICATION) {
      footerText = 'This code expires in 10 minutes';
    }

    if (!footerText) return null;

    return (
      <div className="px-3 pb-2 text-[11px] text-gray-400 font-medium">
        <WhatsAppText text={footerText} />
      </div>
    );
  }, [template]);

  // Buttons Content
  const buttonsContent = useMemo(() => {
    if (!template?.buttons?.length) return null;

    return (
      <div className="border-t border-gray-50 flex flex-col divide-y divide-gray-50">
        {template.buttons.map((button, idx) => {
          let displayValue = button.text;
          const copyCodeKey = `copy_code_${idx}`;
          const urlKey = `url_${idx}`;

          if (button.type === 'copy_code') {
            const val = buttonParams[copyCodeKey] || 'CODE123';
            displayValue = `${button.text || 'Copy Code'}: ${val}`;
          } else if (button.type === 'url' && buttonParams[urlKey]) {
            displayValue = `${button.text || 'Visit Website'} →`;
          }

          return (
            <div
              key={idx}
              className="py-2.5 px-4 text-center text-primary-600 font-semibold text-[13px] hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-center space-x-2">
                {button.type === 'url' && (
                  <Play className="w-3 h-3 fill-primary-600 rotate-[-45deg]" />
                )}
                {button.type === 'phone_number' && (
                  <Play className="w-3 h-3 fill-primary-600" />
                )}
                <span>{displayValue}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [template, buttonParams]);

  const MessageBubble = () => (
    <div
      className={`w-full max-w-[300px] ${!showMobilePreview ? className : ''}`}
    >
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 relative overflow-hidden">
        {headerContent}
        {bodyContent}
        {footerContent}

        {/* Timestamp */}
        <div className="px-3 pb-2 text-right">
          <span className="text-[10px] text-gray-400">12:34</span>
        </div>

        {buttonsContent}

        {/* Tail */}
        {showTail && (
          <div
            className="absolute -left-1 top-0 w-0 h-0"
            style={{
              borderTop: '8px solid transparent',
              borderBottom: '8px solid transparent',
              borderRight: '8px solid white',
            }}
          ></div>
        )}
      </div>
    </div>
  );

  if (!showMobilePreview) {
    return <MessageBubble />;
  }

  return (
    <div
      className={`w-full max-w-[320px] rounded-[32px] border-[8px] border-gray-800 shadow-2xl bg-[#E5DDD5] overflow-hidden relative aspect-[9/16] ${className}`}
    >
      {/* Status Bar */}
      <div className="bg-[#075E54] h-6 w-full flex items-center justify-between px-4">
        <span className="text-white text-[10px] font-medium">12:34</span>
        <div className="flex space-x-1">
          <div className="w-2 h-2 rounded-full border border-white"></div>
          <div className="w-2 h-2 rounded-full border border-white"></div>
        </div>
      </div>

      {/* Chat Header */}
      <div className="bg-[#075E54] p-3 flex items-center space-x-3 shadow-md">
        {businessLogo ? (
          <img
            src={businessLogo}
            alt="Business profile"
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-bold">
            {businessName?.[0] || 'B'}
          </div>
        )}
        <div>
          <h5 className="text-white text-xs font-bold leading-none">
            {businessName?.length > 26
              ? `${businessName.substring(0, 26)}...`
              : businessName || 'Business'}
          </h5>
          <span className="text-white/70 text-[10px]">Business Account</span>
        </div>
      </div>

      {/* Chat Area */}
      <div
        className="flex-1 p-4 relative overflow-y-auto h-full"
        style={{
          backgroundImage: `url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")`,
          backgroundSize: 'cover',
          opacity: 0.8,
        }}
      >
        {template ? (
          <MessageBubble />
        ) : (
          <div className="flex items-center justify-center text-center px-6">
            <div className="bg-white/90 backdrop-blur-sm p-4 rounded-2xl border border-white/50 shadow-lg">
              <svg
                className="w-10 h-10 text-primary-500 mx-auto mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              <p className="text-xs font-semibold text-gray-600">
                Select a template to see how it looks here
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;
